import React from "react";
import ReactDOM from "react-dom/client";
import { createHashRouter, RouterProvider } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { HelmetProvider } from "react-helmet-async";
import { initializeIcons } from "@fluentui/react";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";

import "./index.css";

import Chat from "./pages/chat/Chat";
import ChatMobile from "./pages/chat/ChatMobile";
import LayoutWrapper from "./layoutWrapper";
import i18next from "./i18n/config";
import { msalConfig, useLogin } from "./authConfig";
import { SHOW_AUDIT, SHOW_CLINICAL_LAB } from "./featureFlags";
import { ClinicalProvider } from "./clinicalContext";

initializeIcons();

const router = createHashRouter([
    {
        path: "/",
        element: <LayoutWrapper />,
        children: [
            {
                index: true,
                lazy: () => import("./pages/caseload/Caseload")
            },
            {
                path: "browse",
                lazy: () => import("./pages/browse/Browse")
            },
            {
                path: "trajectory",
                lazy: () => import("./pages/trajectory/PatientTrajectory")
            },
            ...(SHOW_CLINICAL_LAB
                ? [
                      {
                          path: "trajectory-lab",
                          lazy: () => import("./pages/trajectory-lab/TrajectoryLab")
                      }
                  ]
                : []),
            ...(SHOW_CLINICAL_LAB
                ? [
                      {
                          path: "clinical-lab",
                          lazy: () => import("./pages/clinical-lab/ClinicalLab")
                      }
                  ]
                : []),
            {
                path: "note/:noteId",
                lazy: () => import("./pages/note/Note")
            },
            {
                path: "qa",
                lazy: () => import("./pages/ask/Ask")
            },
            {
                path: "caseload",
                lazy: () => import("./pages/caseload/Caseload")
            },
            {
                path: "brief",
                lazy: () => import("./pages/brief/PatientBrief")
            },
            {
                path: "chat",
                element: <Chat />
            },
            ...(SHOW_AUDIT
                ? [
                      {
                          path: "audit",
                          lazy: () => import("./pages/audit/Audit")
                      },
                      {
                          path: "audit/:assessmentId",
                          lazy: () => import("./pages/audit/NoteAudit")
                      }
                  ]
                : []),
            {
                path: "help",
                lazy: () => import("./pages/help/Help")
            },
            {
                path: "*",
                lazy: () => import("./pages/NoPage")
            }
        ]
    },
    {
        path: "/mobile",
        element: <LayoutWrapper layout="mobile" />,
        children: [
            {
                path: "chat",
                element: <ChatMobile />
            }
        ]
    }
]);

// Create MSAL instance at module level (before async init)
const msalInstance = useLogin ? new PublicClientApplication(msalConfig) : null;

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

// Wait for MSAL to initialize and restore session before rendering
async function initializeApp() {
    if (useLogin && msalInstance) {
        await msalInstance.initialize();
        await msalInstance.handleRedirectPromise();

        // Set active account if one exists in cache
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            msalInstance.setActiveAccount(accounts[0]);
        }
    }

    root.render(
        <React.StrictMode>
            <HelmetProvider>
                <I18nextProvider i18n={i18next}>
                    {useLogin && msalInstance ? (
                        <MsalProvider instance={msalInstance}>
                            <ClinicalProvider>
                                <RouterProvider router={router} />
                            </ClinicalProvider>
                        </MsalProvider>
                    ) : (
                        <ClinicalProvider>
                            <RouterProvider router={router} />
                        </ClinicalProvider>
                    )}
                </I18nextProvider>
            </HelmetProvider>
        </React.StrictMode>
    );
}

initializeApp();
